import React, { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  Row,
  Col,
  Table,
  Form,
  Input,
  Switch,
  Button,
  Typography,
  Card,
  Space,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { usePaquers } from 'services/PaquerService';
import { useCreateNotification } from 'services/NotificationService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CreateNotificationModal = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedPaquers, setSelectedPaquers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendImage, setSendImage] = useState(false);
  const [sendAction, setSendAction] = useState(false);

  // Observar cambios en el campo imageUrl del formulario
  const imageUrl = Form.useWatch('imageUrl', form);

  const { data: paquers = [], isLoading: loadingPaquers } = usePaquers();
  const createNotificationMutation = useCreateNotification();

  // Filtrar paquers por término de búsqueda
  const filteredPaquers = useMemo(() => {
    if (!searchTerm) return paquers;
    return paquers.filter(
      (paquer) =>
        paquer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        paquer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        paquer.email?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [paquers, searchTerm]);

  // Manejar selección de paquers
  const handlePaquerSelection = useCallback((selectedRowKeys) => {
    setSelectedPaquers(selectedRowKeys);
  }, []);

  // Manejar envío del formulario
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (selectedPaquers.length === 0) {
        message.error('Debe seleccionar al menos un paquer');
        return;
      }

      const notificationData = {
        push: values.push || false,
        title: values.title,
        description: values.description,
        type: 'FROM_OPERATIONS',
        sendEmail: false,
        scope: 'DALI_MOBILE',
        icon: 'LETTER',
        recipients: selectedPaquers,
      };

      // Agregar imagen principal si existe
      if (sendImage && values.imageUrl) {
        notificationData.images = {
          main: {
            src: values.imageUrl,
            shape: 'SQUARE',
          },
        };
      }

      // Agregar acción URL si existe
      if (sendAction && values.actionTitle && values.actionUrl) {
        notificationData.action = {
          type: 'URL',
          objectiveId: values.actionUrl,
        };
        notificationData.url = values.actionUrl;
        notificationData.buttons = [
          {
            title: values.actionTitle,
            type: 'PRIMARY',
            action: {
              type: 'URL',
              objectiveId: values.actionUrl,
            },
          },
        ];
      }

      await createNotificationMutation.mutateAsync(notificationData);

      message.success('Notificación creada exitosamente');
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating notification:', error);
      message.error('Error al crear la notificación');
    }
  }, [form, selectedPaquers, createNotificationMutation, onSuccess]);

  // Manejar cierre del modal
  const handleClose = useCallback(() => {
    form.resetFields();
    setSelectedPaquers([]);
    setSearchTerm('');
    setSendImage(false);
    setSendAction(false);
    onClose();
  }, [form, onClose]);

  // Configuración de la tabla de paquers
  const paquerColumns = [
    {
      title: 'ID',
      key: 'id',
      render: (_, record) => `${record.id}`.trim(),
      ellipsis: true,
    },
    {
      title: 'Nombre',
      key: 'name',
      render: (_, record) =>
        `${record.name || ''} ${record.lastName || ''}`.trim(),
      ellipsis: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: status === 1 ? '#52c41a' : '#ff4d4f' }}>
          {status === 1 ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedPaquers,
    onChange: handlePaquerSelection,
  };

  return (
    <Modal
      title="Crear Nueva Notificación"
      open={visible}
      onCancel={handleClose}
      width={1400}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createNotificationMutation.isPending}
          onClick={handleSubmit}
        >
          Crear Notificación
        </Button>,
      ]}
      styles={{
        body: { maxHeight: '80vh', overflowY: 'auto' },
      }}
    >
      <Row gutter={[24, 16]}>
        {/* Panel izquierdo - Selección de destinatarios */}
        <Col xs={24} lg={12}>
          <Card title="Seleccionar Destinatarios" size="small">
            <Space
              direction="vertical"
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Input
                placeholder="Buscar paquers por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
              <Text type="secondary">
                {selectedPaquers.length} paquer(s) seleccionado(s)
              </Text>
            </Space>

            <Table
              rowSelection={rowSelection}
              columns={paquerColumns}
              dataSource={filteredPaquers}
              rowKey="id"
              size="small"
              loading={loadingPaquers}
              tableLayout="auto"
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} paquers`,
              }}
              scroll={{ y: 400 }}
            />
          </Card>
        </Col>

        {/* Panel derecho - Información de la notificación */}
        <Col xs={24} lg={12}>
          <Card title="Información de la Notificación" size="small">
            <Form form={form} layout="vertical" requiredMark={false}>
              <Form.Item
                name="title"
                label="Título"
                rules={[
                  { required: true, message: 'El título es requerido' },
                  {
                    max: 40,
                    message: 'El título no puede exceder 40 caracteres',
                  },
                ]}
              >
                <Input
                  placeholder="Ingrese el título de la notificación"
                  maxLength={40}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Descripción"
                rules={[
                  { required: true, message: 'La descripción es requerida' },
                  {
                    max: 256,
                    message: 'La descripción no puede exceder 256 caracteres',
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Ingrese la descripción de la notificación"
                  maxLength={256}
                  showCount
                />
              </Form.Item>

              <Form.Item name="push" label="Envío Push" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item label="¿Enviar imagen?" valuePropName="checked">
                <Switch checked={sendImage} onChange={setSendImage} />
              </Form.Item>

              {sendImage && (
                <Form.Item
                  name="imageUrl"
                  label="URL de la imagen"
                  rules={[
                    {
                      required: true,
                      message: 'La URL de la imagen es requerida',
                    },
                    { type: 'url', message: 'Debe ser una URL válida' },
                  ]}
                >
                  <Input placeholder="https://ejemplo.com/imagen.jpg" />
                </Form.Item>
              )}

              {sendImage && imageUrl && (
                <Form.Item label="Previsualización">
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt="Previsualización"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{ display: 'none', color: '#ff4d4f' }}>
                      Error al cargar la imagen
                    </div>
                  </div>
                </Form.Item>
              )}

              <Form.Item label="¿Enviar acción?" valuePropName="checked">
                <Switch checked={sendAction} onChange={setSendAction} />
              </Form.Item>

              {sendAction && (
                <>
                  <Form.Item
                    name="actionTitle"
                    label="Título del botón"
                    rules={[
                      {
                        required: true,
                        message: 'El título del botón es requerido',
                      },
                    ]}
                  >
                    <Input placeholder="Ver detalles" />
                  </Form.Item>

                  <Form.Item
                    name="actionUrl"
                    label="URL de acción"
                    rules={[
                      {
                        required: true,
                        message: 'La URL de acción es requerida',
                      },
                      { type: 'url', message: 'Debe ser una URL válida' },
                    ]}
                  >
                    <Input placeholder="https://ejemplo.com/accion" />
                  </Form.Item>
                </>
              )}
            </Form>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default CreateNotificationModal;
